import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Phone, UserPlus, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { EmployeeFormDialog } from "@/pages/admin/EmployeeFormDialog";
import { useI18n } from "@/i18n/I18nProvider";
import { api, errorKey, formatPhone } from "@/lib/api";

const StatusBadge = ({ active, t }) => (
  <span className={`w-fit self-start rounded-full px-2.5 sm:self-center py-1 text-[11px] font-bold uppercase tracking-wider ${active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
    {t(active ? "common.active" : "common.inactive")}
  </span>
);

const EmployeeRow = ({ emp, onEdit, t }) => (
  <li data-testid={`employee-row-${emp.id}`}
    className="flex flex-col gap-3 p-4 sm:grid sm:grid-cols-[minmax(0,1fr)_170px_100px_110px] sm:items-center sm:gap-6 sm:px-6">
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#000B45] text-sm font-bold text-[#FEB601]">
        {emp.first_name[0]}{emp.last_name[0]}
      </div>
      <p className="truncate font-semibold text-[#111827]" data-testid="employee-row-name">{emp.first_name} {emp.last_name}</p>
    </div>
    <p className="flex items-center gap-2 font-mono text-sm text-gray-700"><Phone className="h-3.5 w-3.5 text-gray-400" />{formatPhone(emp.phone)}</p>
    <StatusBadge active={emp.active} t={t} />
    <button type="button" onClick={() => onEdit(emp)} data-testid={`employee-edit-${emp.id}`}
      className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] px-4 text-sm font-semibold text-[#000B45] transition-colors hover:bg-[#000B45] hover:text-white">
      <Pencil className="h-4 w-4" /> {t("common.edit")}
    </button>
  </li>
);

export default function Employees() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(params.get("new") === "1");

  const load = useCallback(() => {
    setError("");
    api.get("/employees").then((r) => setList(r.data.employees)).catch((e) => setError(errorKey(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDialog = (emp = null) => { setEditing(emp); setOpen(true); };
  const onOpenChange = (v) => { setOpen(v); if (!v && params.get("new")) setParams({}); };

  const addBtn = (
    <button type="button" onClick={() => openDialog()} data-testid="employee-create-button"
      className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-[#FEB601] px-5 text-sm font-bold text-[#000B45] transition-colors hover:bg-[#E5A300] sm:w-auto">
      <UserPlus className="h-4 w-4" /> {t("employees.add")}
    </button>
  );

  return (
    <div>
      <PageHeader title={t("employees.title")} subtitle={t("employees.subtitle")} action={addBtn} testId="employees-title" />
      {error && (
        <div data-testid="employees-error" className="mb-4 flex items-center justify-between rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {t(error)} <button onClick={load} className="font-semibold underline">{t("common.retry")}</button>
        </div>
      )}
      {list === null && !error && <div className="h-40 animate-pulse rounded-xl bg-white" />}
      {list?.length === 0 && (
        <EmptyState testId="employees-empty" icon={Users} title={t("employees.empty")} description={t("employees.emptyHint")} />
      )}
      {list?.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
          <p className="border-b border-[#E5E7EB] px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 sm:px-6" data-testid="employees-count">
            {t("employees.count", { count: list.length })}
          </p>
          <ul className="divide-y divide-[#E5E7EB]" data-testid="employees-list">
            {list.map((emp) => <EmployeeRow key={emp.id} emp={emp} onEdit={openDialog} t={t} />)}
          </ul>
        </div>
      )}
      <EmployeeFormDialog open={open} onOpenChange={onOpenChange} employee={editing} onSaved={load} />
    </div>
  );
}
